import { resolveStandardFieldConfig } from '../constants/registration-standard-fields';
import type { CreateFormRequestDto } from '../models/dto/forms.dto';
import {
	DEFAULT_FORM_SETTINGS,
	type FormFieldDefinitionModel,
	type FormPurpose,
	type FormRuleModel,
	type FormSettingsModel,
} from './form-types';
import {
	REGISTRATION_FORM_KEY,
	registrationFormFields,
} from './registration-form';

/**
 * Stock forms (owner decision F12; docs/claude/forms/01-design.md §7): Contact,
 * Registration and Blank are starting points Workd ships. "New form" copies one into the
 * brand, and from then on the copy is the brand's own: every field and rule is editable,
 * and a later change here never reaches a form already created from it.
 */

export const FORM_TEMPLATE_KEYS = ['contact', 'registration', 'blank'] as const;

export type FormTemplateKey = (typeof FORM_TEMPLATE_KEYS)[number];

export interface FormTemplateModel {
	label: string;
	description: string;
	purpose: FormPurpose;
	defaultName: string;
	defaultFormKey: string;
	fields: Array<FormFieldDefinitionModel>;
	rules: Array<FormRuleModel>;
	/**
	 * PD-17590 — a new form starts with Accept access requests on: its access request
	 * questions and actions are added when the form is created (form-access-request-setup.ts).
	 */
	accessRequests: boolean;
}

type TemplateFieldModel = Partial<FormFieldDefinitionModel> &
	Pick<FormFieldDefinitionModel, 'fieldKey' | 'label' | 'fieldType'>;

function field(input: TemplateFieldModel): FormFieldDefinitionModel {
	return {
		adminLabel: null,
		placeholder: null,
		helpText: null,
		isRequired: false,
		sortOrder: 0,
		defaultValue: null,
		selectOptions: null,
		validationRules: null,
		identity: null,
		consentPurpose: null,
		policyVersions: null,
		termsBody: null,
		termsUrl: null,
		termsLinkText: null,
		includeInNotification: true,
		...input,
	};
}

/** Display order is the array order; sortOrder is written to match it. */
function inOrder(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
): Array<FormFieldDefinitionModel> {
	return fields.map((definition, index) => ({
		...definition,
		sortOrder: index,
	}));
}

function contactTemplate(): FormTemplateModel {
	return {
		label: 'Contact',
		description:
			'Name, email or phone, company and a message. For a "Contact us" page.',
		purpose: 'contact',
		defaultName: 'Contact',
		defaultFormKey: 'contact',
		fields: inOrder([
			field({
				fieldKey: 'firstName',
				label: 'First Name',
				fieldType: 'text',
				identity: 'first_name',
				isRequired: true,
			}),
			field({
				fieldKey: 'lastName',
				label: 'Last Name',
				fieldType: 'text',
				identity: 'last_name',
				isRequired: true,
			}),
			field({
				fieldKey: 'email',
				label: 'Email',
				fieldType: 'email',
				identity: 'email',
			}),
			field({
				fieldKey: 'phone',
				label: 'Phone',
				fieldType: 'phone',
				identity: 'phone',
			}),
			field({
				fieldKey: 'company',
				label: 'Company',
				fieldType: 'text',
				identity: 'company',
			}),
			field({
				fieldKey: 'message',
				label: 'Message',
				fieldType: 'textarea',
				identity: 'message',
				isRequired: true,
			}),
		]),
		// Either way of replying will do, but one of them is needed to reply at all.
		rules: [{ rule: 'at_least_one', fieldKeys: ['email', 'phone'] }],
		accessRequests: false,
	};
}

/**
 * Today's storefront registration form at its defaults, built by the same code as the
 * migrated forms (registration-form.ts), so a new brand's registration asks what the
 * Register page asked: field keys, order and wording unchanged.
 *
 * PD-17590 — a new Registration form comes with access requests wired up (the ticket's
 * rollout: existing forms are untouched, new ones have it). The server adds the questions
 * and actions when it creates the form, the same way the Details tab switch does.
 */
function registrationTemplate(): FormTemplateModel {
	return {
		label: 'Registration',
		description:
			'The storefront customer registration: contact, company and address, with access requests from existing customers turned on. The Register page uses it.',
		purpose: 'registration',
		defaultName: 'Registration',
		defaultFormKey: REGISTRATION_FORM_KEY,
		fields: registrationFormFields(resolveStandardFieldConfig([], null), []),
		rules: [],
		accessRequests: true,
	};
}

function blankTemplate(): FormTemplateModel {
	return {
		label: 'Blank',
		description: 'No questions yet; add your own.',
		purpose: 'custom',
		defaultName: 'New form',
		defaultFormKey: 'form',
		fields: [],
		rules: [],
		accessRequests: false,
	};
}

export const FORM_TEMPLATES: Readonly<
	Record<FormTemplateKey, FormTemplateModel>
> = {
	contact: contactTemplate(),
	registration: registrationTemplate(),
	blank: blankTemplate(),
};

/**
 * The create request for a new form copied from a stock form. Everything is copied, so
 * editing the new form never changes the template and a later template change never
 * reaches the form.
 */
export function buildFormFromTemplate(
	key: FormTemplateKey,
	target: {
		name: string;
		formKey: string;
		fkPortalSiteId: number | null;
	},
): CreateFormRequestDto {
	const template = FORM_TEMPLATES[key];
	const settings: FormSettingsModel = {
		...DEFAULT_FORM_SETTINGS,
		blocklist: {
			...DEFAULT_FORM_SETTINGS.blocklist,
			emailDomains: [...DEFAULT_FORM_SETTINGS.blocklist.emailDomains],
			emails: [...DEFAULT_FORM_SETTINGS.blocklist.emails],
			ips: [...DEFAULT_FORM_SETTINGS.blocklist.ips],
			keywords: [...DEFAULT_FORM_SETTINGS.blocklist.keywords],
		},
	};

	return {
		name: target.name,
		formKey: target.formKey,
		fkPortalSiteId: target.fkPortalSiteId,
		purpose: template.purpose,
		settings,
		fields: template.fields.map((definition) => ({
			...definition,
			selectOptions:
				definition.selectOptions?.map((option) => ({ ...option })) ?? null,
			validationRules:
				definition.validationRules != null
					? { ...definition.validationRules }
					: null,
		})),
		fieldRules: template.rules.map((rule) => ({
			...rule,
			fieldKeys: [...rule.fieldKeys],
		})),
		...(template.accessRequests ? { accessRequests: true } : {}),
	};
}
